import type { Service } from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  PageHeader,
  Select,
  SkeletonRows,
  Tabs,
  Textarea,
} from '@pm/ui';
import { Briefcase, FileSignature, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney } from '@/lib/format';
import {
  useClients,
  useContracts,
  useInvalidating,
  useProposals,
  useServices,
  useTaskBlocks,
  useWorkflowTemplates,
} from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { ContractBadge, ProposalBadge } from './commercial-bits';

type Tab = 'services' | 'proposals' | 'contracts';

/** SPEC §8-§10 — catálogo, propostas e contratos. */
export function CommercialPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'proposals';
  const { can } = useAuth();
  const tabs = [
    ...(can('proposals.view') ? [{ value: 'proposals' as const, label: 'Propostas' }] : []),
    ...(can('contracts.view') ? [{ value: 'contracts' as const, label: 'Contratos' }] : []),
    ...(can('services.view') ? [{ value: 'services' as const, label: 'Serviços' }] : []),
  ];
  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Comercial"
        subtitle="Serviços do catálogo, propostas com valores congelados e contratos que disparam workflows ao ativar."
      />
      <Tabs value={tab} onChange={(t) => setParams({ tab: t })} tabs={tabs} />
      {tab === 'services' ? (
        <ServicesSection />
      ) : tab === 'contracts' ? (
        <ContractsSection />
      ) : (
        <ProposalsSection />
      )}
    </div>
  );
}

function ServicesSection() {
  const toast = useToast();
  const { can } = useAuth();
  const { data = [], isLoading } = useServices(true);
  const [editing, setEditing] = useState<Service | 'new' | null>(null);
  const [remove, setRemove] = useState<Service | null>(null);
  const del = useInvalidating((id: string) => api.delete(`/services/${id}`), [['services']]);
  const manage = can('services.manage');
  return (
    <div className="flex flex-col gap-gap">
      {manage ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus /> Novo serviço
          </Button>
        </div>
      ) : null}
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="Nenhum serviço"
            description="Cadastre o que a agência vende: nome, categoria, preço base e o workflow que a ativação do contrato dispara."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Serviço</th>
                <th className="h-8 px-3 text-left font-medium">Categoria</th>
                <th className="h-8 px-3 text-right font-medium">Preço base</th>
                <th className="h-8 px-3 text-left font-medium">Workflows</th>
                <th className="h-8 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr
                  key={s.id}
                  className="h-row border-b border-border last:border-b-0 hover:bg-surface-2"
                >
                  <td className="px-3">
                    <span className="font-medium">{s.name}</span>
                    {s.status === 'INACTIVE' ? (
                      <Badge tone="neutral" className="ml-2">
                        inativo
                      </Badge>
                    ) : null}
                    {s.description ? (
                      <span className="ml-2 text-sm text-text-3">{s.description}</span>
                    ) : null}
                  </td>
                  <td className="px-3 text-sm text-text-2">{s.category ?? '—'}</td>
                  <td className="px-3 text-right font-mono text-sm">{fmtMoney(s.base_price)}</td>
                  <td className="px-3">
                    <span className="flex flex-wrap gap-1">
                      {s.workflow_templates.map((w) => (
                        <Badge key={w.id} mono={false} tone="accent">
                          {w.name}
                        </Badge>
                      ))}
                      {s.task_blocks.map((b) => (
                        <Badge key={b.id} mono={false} tone="neutral">
                          {b.name}
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td className="px-3 text-right">
                    {manage ? (
                      <span className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Editar"
                          onClick={() => setEditing(s)}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Excluir"
                          onClick={() => setRemove(s)}
                        >
                          <Trash2 />
                        </Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing ? (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent title={editing === 'new' ? 'Novo serviço' : 'Editar serviço'} size="lg">
            <ServiceForm
              service={editing === 'new' ? null : editing}
              onDone={() => setEditing(null)}
            />
          </DialogContent>
        </Dialog>
      ) : null}
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir ${remove?.name ?? ''}?`}
        description="Propostas e contratos existentes mantêm o snapshot."
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Serviço excluído'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function ServiceForm({ service, onDone }: { service: Service | null; onDone: () => void }) {
  const toast = useToast();
  const { data: templates = [] } = useWorkflowTemplates();
  const { data: blocks = [] } = useTaskBlocks();
  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [category, setCategory] = useState(service?.category ?? '');
  const [price, setPrice] = useState(service?.base_price ?? '');
  const [status, setStatus] = useState(service?.status ?? 'ACTIVE');
  const [wfIds, setWfIds] = useState<string[]>(service?.workflow_templates.map((w) => w.id) ?? []);
  const [blockIds, setBlockIds] = useState<string[]>(service?.task_blocks.map((b) => b.id) ?? []);
  const save = useInvalidating(
    (input: Record<string, unknown>) =>
      service
        ? api.patch<Service>(`/services/${service.id}`, input)
        : api.post<Service>('/services', input),
    [['services']],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            name,
            description: description || null,
            category: category || null,
            base_price: price === '' ? null : Number(price.replace(',', '.')).toFixed(2),
            status,
            workflow_template_ids: wfIds,
            task_block_ids: blockIds,
          },
          {
            onSuccess: () => (toast.success('Serviço salvo'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Nome" htmlFor="sv-name">
        <Input
          id="sv-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Gestão de tráfego"
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Categoria" htmlFor="sv-cat">
          <Input
            id="sv-cat"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Mídia, Design…"
          />
        </Field>
        <Field label="Preço base (R$)" htmlFor="sv-price">
          <Input
            id="sv-price"
            type="number"
            step="0.01"
            min={0}
            mono
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </Field>
        <Field label="Status" htmlFor="sv-status">
          <Select
            id="sv-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'INACTIVE')}
          >
            <option value="ACTIVE">Ativo</option>
            <option value="INACTIVE">Inativo</option>
          </Select>
        </Field>
      </div>
      <Field label="Descrição" htmlFor="sv-desc">
        <Textarea
          id="sv-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[64px]"
        />
      </Field>
      <div className="flex flex-col gap-1.5">
        <LabelCaps>Workflows disparados na ativação do contrato</LabelCaps>
        <div className="flex flex-wrap gap-2">
          {templates
            .filter((t) => t.published)
            .map((t) => (
              <label
                key={t.id}
                className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
              >
                <Checkbox
                  checked={wfIds.includes(t.id)}
                  onCheckedChange={(v) =>
                    setWfIds(v ? [...wfIds, t.id] : wfIds.filter((x) => x !== t.id))
                  }
                />
                {t.name}
              </label>
            ))}
          {templates.filter((t) => t.published).length === 0 ? (
            <span className="text-sm text-text-3">Nenhum workflow publicado.</span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <LabelCaps>Blocos de tarefas relacionados</LabelCaps>
        <div className="flex flex-wrap gap-2">
          {blocks.map((b) => (
            <label
              key={b.id}
              className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
            >
              <Checkbox
                checked={blockIds.includes(b.id)}
                onCheckedChange={(v) =>
                  setBlockIds(v ? [...blockIds, b.id] : blockIds.filter((x) => x !== b.id))
                }
              />
              {b.name}
            </label>
          ))}
          {blocks.length === 0 ? <span className="text-sm text-text-3">Nenhum bloco.</span> : null}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={name.trim().length < 2}
        >
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}

function ProposalsSection() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const [status, setStatus] = useState('');
  const [create, setCreate] = useState(false);
  const { data, isLoading, isError, error, refetch } = useProposals({
    status: status ? [status as 'DRAFT'] : undefined,
  });
  const { data: clients } = useClients({ status: 'ACTIVE' });
  const [clientId, setClientId] = useState('');
  const createP = useInvalidating(
    (cid: string) => api.post<{ id: string }>('/proposals', { client_id: cid }),
    [['proposals']],
  );
  const rows = data?.data ?? [];
  return (
    <div className="flex flex-col gap-gap">
      <div className="flex items-center gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-control-sm w-[160px] text-sm"
          aria-label="Status"
        >
          <option value="">Todos</option>
          <option value="DRAFT">Rascunho</option>
          <option value="SENT">Enviadas</option>
          <option value="APPROVED">Aprovadas</option>
          <option value="REJECTED">Rejeitadas</option>
          <option value="EXPIRED">Expiradas</option>
        </Select>
        {can('proposals.create') ? (
          <Button variant="primary" className="ml-auto" onClick={() => setCreate(true)}>
            <Plus /> Nova proposta
          </Button>
        ) : null}
      </div>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhuma proposta"
            description="Crie uma proposta para um cliente e adicione itens do catálogo."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Nº</th>
                <th className="h-8 px-3 text-left font-medium">Cliente</th>
                <th className="h-8 px-3 text-left font-medium">Status</th>
                <th className="h-8 px-3 text-right font-medium">Total</th>
                <th className="h-8 px-3 text-left font-medium">Validade</th>
                <th className="h-8 px-3 text-left font-medium">Criada</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/comercial/propostas/${p.id}`)}
                >
                  <td className="px-3 font-mono text-sm">#{p.number}</td>
                  <td className="px-3">
                    <Link
                      to={`/comercial/propostas/${p.id}`}
                      className="font-medium hover:text-accent"
                    >
                      {p.client.name}
                    </Link>
                  </td>
                  <td className="px-3">
                    <ProposalBadge status={p.status} />
                  </td>
                  <td className="px-3 text-right font-mono text-sm">{fmtMoney(p.total)}</td>
                  <td className="px-3 font-mono text-xs text-text-3">
                    {p.valid_until ? fmtDate(`${p.valid_until}T12:00:00`) : '—'}
                  </td>
                  <td className="px-3 font-mono text-xs text-text-3">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent title="Nova proposta" size="sm">
          <Field label="Cliente" htmlFor="pp-client">
            <Select id="pp-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Selecione…</option>
              {(clients?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.trade_name ?? c.legal_name}
                </option>
              ))}
            </Select>
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreate(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={!clientId}
              loading={createP.isPending}
              onClick={() =>
                createP.mutate(clientId, {
                  onSuccess: (p) => (setCreate(false), navigate(`/comercial/propostas/${p.id}`)),
                  onError: (e) => toast.error(e),
                })
              }
            >
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContractsSection() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const { data, isLoading, isError, error, refetch } = useContracts({
    status: status ? [status as 'ACTIVE'] : undefined,
  });
  const rows = data?.data ?? [];
  return (
    <div className="flex flex-col gap-gap">
      <div className="flex items-center gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-control-sm w-[160px] text-sm"
          aria-label="Status"
        >
          <option value="">Todos</option>
          <option value="DRAFT">Rascunho</option>
          <option value="ACTIVE">Ativos</option>
          <option value="SUSPENDED">Suspensos</option>
          <option value="ENDED">Encerrados</option>
          <option value="CANCELED">Cancelados</option>
        </Select>
        <span className="ml-auto text-xs text-text-3">
          Contratos nascem da aprovação de uma proposta.
        </span>
      </div>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title="Nenhum contrato"
            description="Aprove uma proposta para gerar o contrato."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Nº</th>
                <th className="h-8 px-3 text-left font-medium">Cliente</th>
                <th className="h-8 px-3 text-left font-medium">Status</th>
                <th className="h-8 px-3 text-right font-medium">Total</th>
                <th className="h-8 px-3 text-left font-medium">Vigência</th>
                <th className="h-8 px-3 text-right font-medium">Serviços</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/comercial/contratos/${c.id}`)}
                >
                  <td className="px-3 font-mono text-sm">#{c.number}</td>
                  <td className="px-3">
                    <Link
                      to={`/comercial/contratos/${c.id}`}
                      className="font-medium hover:text-accent"
                    >
                      {c.client.name}
                    </Link>
                  </td>
                  <td className="px-3">
                    <ContractBadge status={c.status} />
                  </td>
                  <td className="px-3 text-right font-mono text-sm">{fmtMoney(c.total)}</td>
                  <td className="px-3 font-mono text-xs text-text-3">
                    {fmtDate(`${c.start_date}T12:00:00`)} →{' '}
                    {c.end_date ? fmtDate(`${c.end_date}T12:00:00`) : 'indeterminado'}
                  </td>
                  <td className="px-3 text-right font-mono text-sm">{c.services.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
